import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SocialLoginButton, SocialProvider } from '@inflearn/app-shell';
import { Box, Button, Flex, Title } from '@inflearn/ds-react-next';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import qs from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import {
  type ClientReferrerRequest,
  ResponseStatus,
} from '../../../../resources/@types/accountSchema';
import { ApiError } from '../../../../resources/ApiError';
import { logger } from '../../../../resources/Logger';
import { ACCOUNT_URL, AUTH_URL } from '../../../constants/urls';
import { useCaptcha } from '../../../hooks/useCaptcha';
import { useIsWebview } from '../../../hooks/useIsWebview';
import { REFER_URL_QUERY_KEY, useReferUrlNavigation } from '../../../hooks/useReferUrlNavigation';
import { useWebviewThemeParameter } from '../../../lib/useThemeParamter';
import { messageToWebview } from '../../../lib/webview';
import { useAuthSignupCreateMutation } from '../../../mutations/useAuthSignupCreateMutation';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { getClientUserRefererObject } from '../../../utils/refererObject';
import { useDocReferrer } from '../../../utils/useDocReferrer';
import EmailPasswordValidator from '../../../validators/EmailPasswordValidator';
import { ERROR_MESSAGE } from '../../../validators/constants';
import { SocialLoginButtonForWebview } from '../../common/SocialLoginButtonForWebview';
import { getSavedInstructorEmail } from '../../util/getSavedInstructorEmail';
import { EmailFormItem, type EmailFormValue } from '../signin/find/password/EmailFormItem';
import {
  type PasswordFormValue,
  PasswordVerifyFormItems,
} from '../signin/update/password/PasswordVerifyFormItems';
import {
  AllowedMarketingCheckBox,
  type AllowedMarketingFormValue,
} from './AllowedMarketingCheckBox';
import { SignupDescription } from './SignupDescription';
import SocialLoginDivider from './SocialLoginDivider';
import { TextSlider } from './TextSlider';

type SignupForm = EmailFormValue & PasswordFormValue & AllowedMarketingFormValue;

const INITIAL_VALUES: SignupForm = {
  email: '',
  password: '',
  passwordConfirm: '',
  allowedMarketing: true,
} as const;

const SOCIAL_LOGIN_BUTTONS = [SocialProvider.Kakao, SocialProvider.Google];
const InAppWebviewUi = {
  Title: () => null,
  SoicalLoginButtons: () => {
    const delegateLoginProcess = (provider: SocialProvider) => {
      messageToWebview('socialSignUp', { provider: provider.toLowerCase() });
    };

    return (
      <>
        {[...SOCIAL_LOGIN_BUTTONS, SocialProvider.Apple].map((socialProvider) => (
          <SocialLoginButtonForWebview
            key={socialProvider}
            provider={socialProvider}
            onClick={() => delegateLoginProcess(socialProvider)}
          />
        ))}
      </>
    );
  },
};

const WebUi = {
  Title: () => {
    const { t } = useTranslation();
    const { isFromInstructorLandingPage } = useFlagByReferrer();

    if (isFromInstructorLandingPage) {
      return (
        <Title order={2} ta="center">
          <Trans
            i18nKey="signup.title.instructor"
            components={{
              br: <br />,
            }}
          />
        </Title>
      );
    }

    return <Title order={2}>{t('회원가입')}</Title>;
  },
  SoicalLoginButtons: () => (
    <>
      {SOCIAL_LOGIN_BUTTONS.map((socialProvider) => (
        <li key={socialProvider}>
          <SocialLoginButton provider={socialProvider} showTooltip={false} isAuthPage />
        </li>
      ))}
    </>
  ),
};

export const SignupPageItem = () => {
  const { t } = useTranslation();
  const isWebview = useIsWebview();
  const savedInstructorEmail = getSavedInstructorEmail();
  const methods = useForm<SignupForm>({
    mode: 'all',
    resolver: classValidatorResolver(EmailPasswordValidator),
    defaultValues: {
      ...INITIAL_VALUES,
      email: savedInstructorEmail ?? INITIAL_VALUES.email,
    },
  });
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { isDarkTheme } = useWebviewThemeParameter();
  const { getReferUrl } = useReferUrlNavigation();
  const captcha = useCaptcha();
  const router = useRouter();

  const { isFromInstructorLandingPage } = useFlagByReferrer();

  const { mutate: signupMutate, isLoading } = useAuthSignupCreateMutation({
    options: {
      onSuccess: (res) => {
        trackMixpanelEvent(eventNames.VIEW_SIGNUP_MAIL_RECEIVED);

        if (isWebview) {
          messageToWebview('sentVerifyingEmail');
        }

        router.push(
          qs.stringifyUrl({
            url: AUTH_URL.SIGNUP_EMAIL_VERIFY(),
            query: {
              referUrl: getReferUrl(),
              email: methods.getValues('email'),
              usersAccessRefererId: res.data?.usersAccessRefererId,
            },
          }),
        );
      },
      onError: (error) => {
        captcha.resetCaptchaToken();

        if (ApiError.is(error, ResponseStatus.CONFLICT)) {
          methods.setError('email', {
            type: 'manual',
            message: ERROR_MESSAGE.EXISTED_EMAIL_REQUEST(),
          });
          return;
        }

        if (ApiError.is(error, ResponseStatus.UNAUTHORIZED)) {
          alert(ERROR_MESSAGE.INVALID_CAPTCHA_TOKEN());

          logger.error(error);

          return;
        }

        methods.setError('email', {
          type: 'manual',
          message: ERROR_MESSAGE.TRY_AGAIN(),
        });

        logger.error(error);
      },
    },
  });
  const [isSignupButtonLoading, setIsSignupButtonLoading] = useState(isLoading);

  const handleFormSubmit: SubmitHandler<SignupForm> = ({ email, password, allowedMarketing }) => {
    setIsSignupButtonLoading(!captcha.isCaptchaTokenExist);

    captcha.runWhenCaptchaExist((captchaToken) => {
      setIsSignupButtonLoading(false);

      signupMutate({
        email,
        password,
        allowedMarketing,
        clientReferrer: {
          ...getClientUserRefererObject(),
          documentReferrer: document.referrer,
          referrer: getReferUrl(),
        } as unknown as ClientReferrerRequest,
        captchaToken,
      });
    });
  };

  const UI = useMemo(() => (isWebview ? InAppWebviewUi : WebUi), [isWebview]);

  useEffect(
    /**
     * NOTE: 지공자 랜딩페이지에서 온 경우, 리다이렉트를 지공자 신청페이지로
     */
    function updateQueryReferrer() {
      if (isFromInstructorLandingPage) {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              [REFER_URL_QUERY_KEY]: ACCOUNT_URL.INSTRUCTOR_APPLICATION(),
            },
          },
          undefined,
          { shallow: true },
        );
      }
    },
    [isFromInstructorLandingPage, router],
  );

  useEffect(() => {
    trackMixpanelEvent(eventNames.VIEW_SIGNUP);
  }, [trackMixpanelEvent]);

  useEffect(() => {
    setIsSignupButtonLoading(isLoading);
  }, [isLoading]);

  return (
    <FormProvider {...methods}>
      <Flex w="100%" direction="column" justify="center" align="center" mb={24} gap={4}>
        <captcha.DevTools />
        <UI.Title />
        <TextSlider />
      </Flex>
      <Flex
        component="form"
        direction="column"
        gap={8}
        w="100%"
        onSubmit={methods.handleSubmit(handleFormSubmit)}
      >
        <EmailFormItem
          showLabel={false}
          size="lg"
          register={methods.register}
          errors={methods.formState.errors}
        />
        <PasswordVerifyFormItems showLabel={false} size="lg" errors={methods.formState.errors} />
        <Button
          type="submit"
          variant="filled"
          color={isDarkTheme ? darkThemeStyle.signupButton.color : 'infgreen'}
          size="lg"
          fullWidth
          loading={isSignupButtonLoading || isLoading}
        >
          {t('auth.signup.submit')}
        </Button>
      </Flex>
      <Box ref={captcha.captchaRef} className={captcha.captchaClassName} mx="auto" />
      <Flex direction="column" align="center" gap="md" mt={16} w="100%">
        <SocialLoginDivider>{t('auth.signup.divider')}</SocialLoginDivider>
        <Flex pos="relative" component={isWebview ? 'div' : 'ul'} gap={isWebview ? 16 : 8}>
          <UI.SoicalLoginButtons />
        </Flex>
      </Flex>
      <Flex direction="column" justify="flex-start" align="center" gap={8} mt={32}>
        <SignupDescription />
        <AllowedMarketingCheckBox register={methods.register} />
      </Flex>
    </FormProvider>
  );
};

export const darkThemeStyle = {
  signupButton: {
    color: 'infgreen.7',
  },
};

function useFlagByReferrer() {
  const referrer = useDocReferrer();
  const isFromInstructorLandingPage = referrer.includes('/open-knowledge');
  return {
    isFromInstructorLandingPage,
  };
}
